import React, { useEffect, useRef, useState } from "react";
import { Desktop, DeviceMobile } from "@phosphor-icons/react";
import api from "@/lib/api";
import { AdminModal, BTN_BLACK, BTN_WHITE } from "@/components/pageSettings/AdminModal";
import { BlockThankYou } from "@/pages/landing/thankYou/ThankYouBlocks";
import { sampleCtx } from "./designPreview";
import { CLASSIC_URL } from "./TemplateGallery";
import { TestPair } from "./TestPair";
import { useIsPhone } from "@/lib/useIsPhone";

const WIDTH = { computer: 1100, phone: 390 };
const NUMS = [["views", "Views"], ["call", "Calls"], ["text", "Texts"], ["article", "Articles"]];

// One design drawn at the chosen screen width, shrunk to fit its column.
function Page({ d, shared, view }) {
  const ref = useRef(null);
  const [col, setCol] = useState(500);
  useEffect(() => {
    const el = ref.current;
    if (!el) { return undefined; }
    const ro = new ResizeObserver(() => setCol(el.clientWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const w = WIDTH[view];
  const zoom = Math.min(1, (col - 2) / w);
  const page = d.blocks?.length
    ? <BlockThankYou blocks={d.blocks} ctx={{ ...sampleCtx(shared), compact: view === "phone" }} />
    : <iframe title={`${d.name} preview`} src={CLASSIC_URL} style={{ width: w, height: 1700, border: 0, display: "block" }} />;
  return (
    <div ref={ref} className="h-[58vh] overflow-y-auto border-2 border-black bg-zinc-100" data-testid="ty-compare-page">
      <div className="mx-auto bg-white" style={{ width: w, zoom }}>{page}</div>
    </div>
  );
}

function Numbers({ row }) {
  const r = row || { views: 0, call: 0, text: 0, article: 0, tap_rate: null };
  return (
    <div className="grid grid-cols-5 border-2 border-black text-center" data-testid="ty-compare-numbers">
      {NUMS.map(([k, label]) => (
        <div key={k} className="border-r border-black p-1.5">
          <div className="font-chivo text-lg font-black tabular-nums">{r[k]}</div>
          <div className="text-[9px] uppercase tracking-widest text-zinc-600">{label}</div>
        </div>
      ))}
      <div className="bg-yellow-50 p-1.5">
        <div className="font-chivo text-lg font-black tabular-nums" data-testid="ty-compare-rate">{r.tap_rate == null ? "—" : `${r.tap_rate}%`}</div>
        <div className="text-[9px] uppercase tracking-widest text-zinc-600">Tap rate</div>
      </div>
    </div>
  );
}

function Side({ side, value, onChange, all, data, shared, view, row, showNums, onDefault }) {
  const d = all.find((x) => x.key === value);
  const isDefault = data.default === value;
  return (
    <div className="flex min-w-0 flex-col gap-2" data-testid={`ty-compare-side-${side}`}>
      <div className="flex items-center gap-2">
        <select className="min-w-0 flex-1 border-2 border-black bg-white px-2 py-1.5 text-sm" value={value} onChange={(e) => onChange(e.target.value)} data-testid={`ty-compare-select-${side}`}>
          <optgroup label="Ready-made">{data.templates.map((t) => <option key={t.key} value={t.key}>{t.name}{t.edited ? " (edited)" : ""}</option>)}</optgroup>
          {data.designs.length > 0 && <optgroup label="Your designs">{data.designs.map((x) => <option key={x.key} value={x.key}>{x.name}</option>)}</optgroup>}
        </select>
        {isDefault ? (
          <span className="border border-black bg-emerald-600 px-1.5 py-1 text-[9px] uppercase tracking-widest text-white" data-testid={`ty-compare-default-${side}`}>Default</span>
        ) : (
          <button type="button" className={`${BTN_BLACK} !px-2 !py-1`} onClick={() => onDefault(value)} data-testid={`ty-compare-make-default-${side}`}>Make default</button>
        )}
      </div>
      {showNums && <Numbers row={row} />}
      {d && <Page key={`${value}-${view}`} d={d} shared={shared} view={view} />}
    </div>
  );
}

export function DesignCompare({ data, shared, onDefault, onClose }) {
  const all = [...data.templates, ...data.designs];
  const [left, setLeft] = useState(data.default || all[0].key);
  const [right, setRight] = useState((all.find((x) => x.key !== (data.default || all[0].key)) || all[0]).key);
  const [view, setView] = useState(useIsPhone() ? "phone" : "computer");
  const [showNums, setShowNums] = useState(true);
  const [stats, setStats] = useState({});
  useEffect(() => {
    api.get("/admin/thankyou-stats", { params: { days: 30 } })
      .then((r) => setStats(Object.fromEntries(r.data.designs.map((x) => [x.key, x])))).catch(() => setStats({}));
  }, []);
  const tab = (v, Icon, label) => (
    <button type="button" onClick={() => setView(v)} data-testid={`ty-compare-view-${v}`}
      className={`${view === v ? BTN_BLACK : BTN_WHITE} !px-2 !py-1`}><Icon size={12} /> {label}</button>
  );
  const sides = { data, all, shared, view, showNums, onDefault };
  return (
    <AdminModal eyebrow="Thank-you designs" title="Compare side by side" onClose={onClose} testId="ty-compare-modal" wide>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {tab("computer", Desktop, "Computer")}
        {tab("phone", DeviceMobile, "Phone")}
        <label className="ml-auto flex items-center gap-2 text-xs">
          <input type="checkbox" checked={showNums} onChange={(e) => setShowNums(e.target.checked)} data-testid="ty-compare-numbers-toggle" />
          Show tap numbers (last 30 days)
        </label>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <Side side="left" value={left} onChange={setLeft} row={stats[left]} {...sides} />
        <Side side="right" value={right} onChange={setRight} row={stats[right]} {...sides} />
      </div>
      {showNums && <p className="mt-2 text-[10px] text-zinc-500">Tap rate = call + text + article taps per 100 views. Bots, tests and previews aren&apos;t counted.</p>}
      <TestPair left={left} right={right} names={Object.fromEntries(all.map((x) => [x.key, x.name]))} />
    </AdminModal>
  );
}
